import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner';
import AuthErrorHandler from '../../utils/authErrorHandler';
import { useMerchant } from '../../contexts/MerchantContext';

const LineCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { fetchMerchant } = useMerchant();
  
  // 狀態控制：processing, binding_phone, verifying_otp, success, error
  const [status, setStatus] = useState('processing'); 
  const [errorMessage, setErrorMessage] = useState('');
  const [callbackDebugInfo, setCallbackDebugInfo] = useState({});
  const [processed, setProcessed] = useState(false);

  // 綁定與驗證用的 State
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState(''); // 6 位數字
  const [otpPrefix, setOtpPrefix] = useState(''); // 4 位字母
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  
  // 倒數計時 State
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  // 倒數計時邏輯
  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [countdown]);

  useEffect(() => {
    if (processed) return;

    const handleCallback = async () => {
      try {
        const sessionId = searchParams.get('session_id') || searchParams.get('session');
        const error = searchParams.get('error');

        setCallbackDebugInfo({
          receivedData: { sessionId, error },
          fullUrl: window.location.href,
          timestamp: new Date().toISOString(),
        });

        if (error) {
          setStatus('error');
          setErrorMessage(AuthErrorHandler.getErrorMessage(error));
          setTimeout(() => navigate(`/login?error=${error}`), 3000);
          return;
        }

        if (sessionId) {
          setCurrentSessionId(sessionId);
          setProcessed(true);
          
          const apiUrl = `${process.env.REACT_APP_API_URL || 'http://192.168.1.112:8040/api'}/auth/line/session/?session_id=${sessionId}`;
          const response = await fetch(apiUrl);

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const sessionData = await response.json();

          if (sessionData.status === 'PENDING_PHONE_BINDING') {
            setStatus('binding_phone');
          } else if (sessionData.tokens) {
            handleLoginSuccess(sessionData);
          }
        }
      } catch (err) {
        console.error('Callback error:', err);
        setStatus('error');
        setErrorMessage('連線伺服器失敗');
      }
    };

    handleCallback();
  }, [processed, navigate, searchParams]);

  // 登入成功後的統一處理
  const handleLoginSuccess = async (data) => {
    const { user, membership, tokens } = data;
    login(tokens, user, membership);
    setStatus('success');

    const mSlug = membership?.merchant_slug;
    if (mSlug) {
      try { await fetchMerchant(mSlug); } catch (e) { console.error(e); }
    }

    let redirectTo = sessionStorage.getItem('auth_redirect');
    if (!redirectTo) {
      redirectTo = mSlug ? `/${mSlug}/profile` : '/profile';
    }
    
    sessionStorage.removeItem('auth_redirect');
    setTimeout(() => navigate(redirectTo), 1500);
  };

  // 步驟 1：請求 SMS 驗證碼
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!phoneNumber || countdown > 0) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/sms/request/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      const data = await response.json();
      if (response.ok) {
        setOtpPrefix(data.prefix);
        setStatus('verifying_otp');
        setCountdown(60); // 開始 60 秒倒數
      } else {
        alert(data.error || '發送失敗');
      }
    } catch (error) {
      alert('系統錯誤，請檢查網路');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 步驟 2：最終驗證並綁定
  const handleVerifyAndBind = async (e) => {
    e.preventDefault();
    if (!otpCode) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/line/bind-phone/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: currentSessionId,
          phone_number: phoneNumber,
          otp_code: otpCode,
          otp_prefix: otpPrefix
        }),
      });

      const data = await response.json();
      if (response.ok) {
        handleLoginSuccess(data);
      } else {
        alert(data.error || '驗證碼錯誤');
      }
    } catch (error) {
      alert('系統錯誤');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'binding_phone':
        return (
          <div className="w-full">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#c66dc7] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">手機號碼</h2>
              <p className="text-gray-500 mt-2">請輸入您的手機號碼以接收驗證碼</p>
            </div>

            <form onSubmit={handleRequestOTP} className="space-y-6">
              <div>
                <input
                  type="tel"
                  placeholder="(如: 0912345678)"
                  className="w-full px-4 py-4 text-xl border-2 border-gray-200 rounded-xl focus:border-[#c66dc7] focus:outline-none transition-all text-center tracking-widest"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !phoneNumber}
                className={`w-full py-4 rounded-xl text-white text-lg font-bold shadow-lg transition-all
                  ${isSubmitting ? 'bg-gray-400' : 'bg-[#c66dc7] hover:bg-red-900 active:scale-95'}
                `}
              >
                {isSubmitting ? '傳送中...' : '傳送簡訊驗證碼'}
              </button>
            </form>
          </div>
        );

      case 'verifying_otp':
        return (
          <div className="w-full">
            <div className="text-center mb-8">
              <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-2">
                簡訊編號：{otpPrefix}
              </div>
              <h2 className="text-2xl font-bold text-gray-800">輸入驗證碼</h2>
              <p className="text-gray-500 mt-2">驗證碼已傳送至 {phoneNumber}</p>
            </div>

            <form onSubmit={handleVerifyAndBind} className="space-y-6">
              <div>
                <input
                  type="number"
                  pattern="\d*"
                  placeholder="6位數字驗證碼"
                  className="w-full px-4 py-4 text-3xl border-2 border-green-200 rounded-xl focus:border-green-500 focus:outline-none transition-all text-center tracking-[0.5em] font-mono"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.slice(0, 6))}
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || otpCode.length !== 6}
                className={`w-full py-4 rounded-xl text-white text-lg font-bold shadow-lg transition-all
                  ${isSubmitting || otpCode.length !== 6 ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700 active:scale-95'}
                `}
              >
                {isSubmitting ? '驗證中...' : '確認並完成加入'}
              </button>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-gray-400 text-sm">{countdown} 秒後可重新傳送</p>
                ) : (
                  <button 
                    type="button"
                    onClick={handleRequestOTP}
                    className="text-[#c66dc7] font-bold text-sm underline"
                  >
                    重新傳送驗證碼
                  </button>
                )}
              </div>
            </form>
          </div>
        );

      case 'processing':
        return (
          <div className="text-center py-10">
            <LoadingSpinner size="large" />
            <p className="mt-4 text-gray-600 font-medium">正在與系統同步資料...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">歡迎回來！</h2>
            <p className="text-gray-500">正在進入您的會員中心...</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-10">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800">認證發生錯誤</h2>
            <p className="text-red-500 mt-2">{errorMessage}</p>
            <button onClick={() => navigate('/login')} className="mt-6 text-blue-600 underline">返回登入頁面</button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white shadow-2xl rounded-3xl p-8 border border-gray-100">
        {renderContent()}
      </div>
    </div>
  );
};

export default LineCallback;